import React, { useEffect, useState } from "react";
import "./addwarehouseuser.css";

// components imports
import Loader from "../../../../../../../../Components/CircularLoader/CircularLoader";
// apis imports
import { registerUser } from "../../../../../../../../services/auth.services";
import { fetchAllRoles } from "../../../../../../../../services/settings.services";
import Countries from "../../../../../../../../data/countries";
import { getEffectiveOwnerId } from "../../../../../../../../helpers/helpers";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useFormat } from "../../../../../../../../i18n/format";

// library imports
import Modal from "@mui/material/Modal";
import Autocomplete from "@mui/material/Autocomplete";
import { TextField } from "@mui/material";
import Form from "react-bootstrap/Form";

const initialLogisticData = {
  address: "",
  city: "",
  country: "",
  name: "",
  passcode: "",
  role: "warehouseUser",
  state: "",
  business_name: "",
  email: "",
  firstname: "",
  lastname: "",
  industry_type: "",
  username: "",
  password: "",
  permissions: [],
};

export default function AddWarehouseUser({
  open,
  setOpen,
  setSnack,
  setRefresh,
}) {
  const { t } = useTranslation("settings");
  const { formatCountry } = useFormat();
  const [formData, setFormData] = useState({ ...initialLogisticData });
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isDefaultWarehouse, setIsDefaultWarehouse] = useState(true);
  const [disabled, setDisabled] = useState(true);

  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const currentUser = useSelector((state) => state.user);

  const handleClose = () => {
    setOpen(false);
    setFormData({ ...initialLogisticData });
    setConfirmPassword("");
    setIsDefaultWarehouse(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };
  const handleFieldChange = (field, value) => {
    if (field === "role") {
      setFormData({
        ...formData,
        [field]: roles.find((r) => r.name === value).name,
        roles: roles.filter((r) => r.name === value),
      });
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      name: formData?.name,
      address: formData?.address,
      city: formData?.city,
      country: formData?.country,
      state: formData?.state,
      passcode: formData?.passcode,
      role: "warehouseUser",
      // Only tag this warehouse to the current user when marked as default.
      ...(isDefaultWarehouse
        ? { current_user_id: getEffectiveOwnerId(currentUser) }
        : {}),
      user: {
        username: formData?.username,
        firstname: formData?.firstname,
        lastname: formData?.lastname,
        business_name: formData?.business_name,
        industry_type: formData?.industry_type,
        email: formData?.email,
        password: formData?.password,
        zip: formData?.passcode,
        city: formData?.city,
        country: formData?.country,
        province: formData?.state,
        address: formData?.address,
      },
    };
    const res = await registerUser(payload);
    setLoading(false);
    if (res.error) {
      let message = "";
      if (res?.error?.response?.data?.username) {
        message = res?.error?.response?.data?.username[0];
      } else if (res?.error?.response?.data?.email) {
        message = res?.error?.response?.data?.email[0];
      } else {
        message = res?.error?.response?.data?.message;
      }
      setSnack((snack) => {
        return {
          ...snack,
          open: true,
          text: message,
          severity: "error",
        };
      });
      setLoading(false);
    } else {
      handleClose();
      setFormData(initialLogisticData);
      setRefresh(Math.floor(Math.random() * 100) + 1);
      setSnack((snack) => {
        return {
          ...snack,
          open: true,
          text: res?.data?.message,
          severity: "success",
        };
      });
    }
  };

  const fetchAllRolesData = async () => {
    const saved = await fetchAllRoles();
    setRoles(
      saved?.data?.result?.data.map((role) => {
        return {
          id: role.id,
          name: role.name,
        };
      }),
    );
  };

  useEffect(() => {
    fetchAllRolesData();
  }, []);

  useEffect(() => {
    if (formData.password !== confirmPassword) {
      setDisabled(true);
    } else {
      setDisabled(false);
    }
  }, [formData.password, confirmPassword]);

  return (
    <div>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={open}
        onClose={handleClose}
        closeAfterTransition
      >
        <Form onSubmit={handleSubmit}>
          <div className="add__warehouse__settings__user_modal">
            <div className="add__warehouse__settings__user_modalheading">
              {t("warehouses.register.title")}
            </div>

            <div className="add__warehouse__settings__user_modalcontent">
              <div className="add__warehouse__settings__user_modalrow">
                <div className="add__warehouse__settings__user_modalinputwrap">
                  <Form.Label>{t("warehouses.register.name")}</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    placeholder={t("warehouses.register.name")}
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                  />
                </div>
                <div className="add__warehouse__settings__user_modalinputwrap">
                  <Form.Label>{t("warehouses.register.address")}</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    placeholder={t("warehouses.register.address")}
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="add__warehouse__settings__user_modalrow">
                <div className="add__warehouse__settings__user_modalinputwrap">
                  <Form.Label>{t("warehouses.register.country")}</Form.Label>
                  <Autocomplete
                    disablePortal
                    id="combo-box-demo"
                    options={Countries}
                    getOptionLabel={(option) => formatCountry(option?.code, option?.name)}
                    sx={{
                      "&  div": {
                        maxHeight: "40px",
                        outline: "none",
                        padding: 0,
                        border: "none",
                        outline: "none",
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        placeholder={t("warehouses.register.country")}
                        className="add__warehouse__settings__user_modalinput"
                      />
                    )}
                    onChange={(e, country) =>
                      handleFieldChange("country", country ? country.code : "")
                    }
                  />
                </div>
                <div className="add__warehouse__settings__user_modalinputwrap">
                  <Form.Label>{t("warehouses.register.state")}</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    placeholder={t("warehouses.register.state")}
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="add__warehouse__settings__user_modalrow">
                <div className="add__warehouse__settings__user_modalinputwrap">
                  <Form.Label>{t("warehouses.register.city")}</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    placeholder={t("warehouses.register.city")}
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                  />
                </div>
                <div className="add__warehouse__settings__user_modalinputwrap">
                  <Form.Label>{t("warehouses.register.postalCode")}</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    placeholder={t("warehouses.register.postalCode")}
                    name="passcode"
                    value={formData.passcode}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="add__warehouse__settings__user_modalrow">
                <div className="add__warehouse__settings__user_modalinputwrap">
                  <Form.Label>{t("fields.businessName")}</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    placeholder={t("fields.businessName")}
                    name="business_name"
                    value={formData.business_name}
                    onChange={handleChange}
                  />
                </div>
                <div className="add__warehouse__settings__user_modalinputwrap">
                  <Form.Label>{t("fields.industryType")}</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    placeholder={t("fields.industryType")}
                    name="industry_type"
                    value={formData.industry_type}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="add__warehouse__settings__user_modalrow">
                <div className="add__warehouse__settings__user_modalinputwrap">
                  <Form.Label>{t("fields.firstName")}</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    placeholder={t("fields.firstName")}
                    name="firstname"
                    value={formData.firstname}
                    onChange={handleChange}
                  />
                </div>
                <div className="add__warehouse__settings__user_modalinputwrap">
                  <Form.Label>{t("fields.lastName")}</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    placeholder={t("fields.lastName")}
                    name="lastname"
                    value={formData.lastname}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="add__warehouse__settings__user_modalrow">
                <div className="add__warehouse__settings__user_modalinputwrap">
                  <Form.Label>{t("fields.username")}</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    placeholder={t("fields.username")}
                    name="username"
                    value={formData.username}
                    onChange={handleChange}
                  />
                </div>
                <div className="add__warehouse__settings__user_modalinputwrap">
                  <Form.Label>{t("fields.email")}</Form.Label>
                  <Form.Control
                    type="email"
                    required
                    placeholder={t("fields.email")}
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="add__warehouse__settings__user_modalrow">
                <div className="add__warehouse__settings__user_modalinputwrap">
                  <Form.Label>{t("fields.password")}</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    placeholder={t("fields.password")}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                  />
                </div>
                <div className="add__warehouse__settings__user_modalinputwrap">
                  <Form.Label>{t("fields.confirmPassword")}</Form.Label>
                  <Form.Control
                    type="text"
                    required
                    placeholder={t("fields.confirmPassword")}
                    value={confirmPassword}
                    name="confirmPassword"
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    isInvalid={formData.password != confirmPassword}
                  />
                </div>
              </div>
              <div className="add__warehouse__settings__user_modalrow">
                <div className="flex items-center gap-2 m-[5px] px-3 h-10 basis-[calc(50%-10px)] border-[0.5px] border-[lightgrey] rounded-[5px] bg-ui-surface">
                  <input
                    type="checkbox"
                    id="default-warehouse-switch"
                    checked={isDefaultWarehouse}
                    onChange={(e) => setIsDefaultWarehouse(e.target.checked)}
                    className="w-4 h-4 cursor-pointer accent-[var(--color-brand)]"
                  />
                  <label
                    htmlFor="default-warehouse-switch"
                    className="m-0 text-[13px] text-[grey] cursor-pointer"
                  >
                    {t("warehouses.register.setDefault")}
                  </label>
                </div>
              </div>
            </div>
            <div className="add__warehouse__settings__user_modalfooter">
              <div
                className="add__warehouse__settings__user_modalcncl"
                onClick={handleClose}
              >
                {t("common:actions.cancel")}
              </div>
              <button
                type="submit"
                className={`add__warehouse__settings__user_modalsave ${
                  (disabled || loading) && "rfsregister__steps_nextbtndisabled"
                }`}
              >
                {loading ? <Loader /> : t("common:actions.submit")}
              </button>
            </div>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
